import {
  Component,
  ElementRef,
  inject,
  signal,
  ViewChild,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter, Input
} from '@angular/core';
import { FriendCardComponent } from "../../../friends/friend-card/friend-card.component";
import { debounceTime, exhaustMap, filter, finalize, map, of, Subscription, tap } from "rxjs";
import { FriendsCrudService } from "app/friends/service/friends-crud.service";
import { IFriend, IFriendsResponse } from "app/friends/model/friends.model";
import { IconComponent } from "../../../shared/icon/icon.component";
import { FormControl, ReactiveFormsModule, Validators } from "@angular/forms";
import { MIN_QUERY_LENGTH } from "../../../friends/friends.const";
import { IMentionUser } from "../story";

@Component({
  selector: 'app-mention-people-screen',
  imports: [FriendCardComponent, IconComponent, ReactiveFormsModule],
  templateUrl: './mention-people-screen.component.html',
  styleUrls: ['./mention-people-screen.component.scss'],
})
export class MentionPeopleScreenComponent implements OnInit, OnDestroy {
  @Input() currentMentions = new Map<number, IMentionUser>();
  @Output() public mentionFriends = new EventEmitter<{
    selected: IMentionUser[];
    unselected: IMentionUser[]
  }>();

  private isFriendsFetching = false;
  private allFriendsLoaded = false;

  protected friendsTotal = signal(0);  private subscriptions = new Subscription();
  private friendsScrollListener?: () => void;
  friendsCrudService = inject(FriendsCrudService);
  friendsList = signal<IFriend[]>([]);
  // friendsList = signal<IFriend[]>([]);
  public searchFriend = new FormControl('', [Validators.minLength(MIN_QUERY_LENGTH)]);

  selectedFriends = new Map<number, IMentionUser>()
  unselectedFriends = new Map<number, IMentionUser>()

  @ViewChild('scrollFriendsContainer', { static: false }) scrollFriendsContainer!: ElementRef;

  ngOnInit() {
    this.getInitialFriends();

    if (this.currentMentions.size) {
      this.selectedFriends = this.currentMentions
    }

    this.subscriptions.add(
        this.searchFriend.valueChanges
            .pipe(
                filter((value): value is string => value !== null),
                map((value: string) => value.trim().toLowerCase()),
                debounceTime(500),
                filter((value: string) => value.length >= MIN_QUERY_LENGTH || !value.length),
            )
            .subscribe((query) => {
              // Reset friends list before filtering
              this.friendsList.set([]);
              this.allFriendsLoaded = false;

              this.loadMoreFriends(query);
            }),
    );
  }

  getInitialFriends() {
    this.subscriptions.add(
        this.friendsCrudService
            .getFriends('', 50)
            .pipe(
                tap(() => (this.isFriendsFetching = true)),
                finalize(() => (this.isFriendsFetching = false)),
            )
            .subscribe((friends: IFriendsResponse) => {
              if (friends.data.length) {
                this.friendsList.set(friends.data);
                this.allFriendsLoaded = friends.total <= this.friendsList().length;
                this.friendsTotal.set(friends.total);
                this.friendsCrudService.setFriendsTotal(friends.total);
              }
            }),
    );
  }

  ngOnDestroy() {
    // this.removeScrollListeners();
    this.subscriptions.unsubscribe();
  }

  trackById(index: number, item: { id: number }) {
    return item.id;
  }

  onFriendsScroll() {
    const container = this.scrollFriendsContainer.nativeElement;
    const bottomReached = container.scrollTop + container.clientHeight >= container.scrollHeight - 200;

    if (bottomReached && !this.allFriendsLoaded && !this.isFriendsFetching) {
      this.loadMoreFriends(this.searchFriend.value || '');
    }
  }


  private loadMoreFriends(query: string): void {
    if (this.allFriendsLoaded || this.isFriendsFetching) return;

    this.isFriendsFetching = true;

    this.friendsCrudService
        .getFriends(query, 50, this.friendsList().length)
        .pipe(
            exhaustMap((friendsResponse) => {
              if (friendsResponse.data.length) {
                this.friendsList.update((list) => [...list, ...friendsResponse.data]);
                this.allFriendsLoaded = friendsResponse.total <= this.friendsList().length;
                this.friendsTotal.set(friendsResponse.total);
                this.friendsCrudService.setFriendsTotal(friendsResponse.total);
              }
              return of(null);
            }),
            finalize(() => (this.isFriendsFetching = false)),
        )
        .subscribe();
  }

  friendSelected(selected: boolean, friend: IFriend) {
    const user: IMentionUser = {
      id: friend.id,
      avatar: friend.avatar,
      userName: friend.userName,
      fullName: friend.fullName,
    }
    if (selected) {
      this.selectedFriends.set(friend.id, user)
      this.unselectedFriends.delete(friend.id);
    } else {
      this.unselectedFriends.set(friend.id, user);
      this.selectedFriends.delete(friend.id);
    }

    this.mentionFriends.emit({
      selected:  Array.from(this.selectedFriends.values()),
      unselected: Array.from(this.unselectedFriends.values())
    })
  }
}
